import React, { useMemo, useState } from "react";
import { useData } from "../context/DataContext";
import { useSearch, matchesQuery } from "../context/SearchContext";
import {
  formatKES,
  computeTenantStatus,
  currentMonth,
  monthOptions,
  formatMonth,
  paidForMonth,
  tenantBalance,
} from "../utils/format";
import Select from "../components/Select";
import TenantsTable from "../components/TenantsTable";
import TenantEditor from "../components/TenantEditor";
import HistoryModal from "../components/HistoryModal";
import BulkActionsBar from "../components/BulkActionsBar";

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "paid", label: "Paid" },
  { value: "partial", label: "Partial" },
  { value: "pending", label: "Pending" },
  { value: "overdue", label: "Overdue" },
];

export default function RentStatus() {
  const { properties, tenants, payments } = useData();
  const { query } = useSearch();
  const [month, setMonth] = useState(currentMonth());
  const [filter, setFilter] = useState("all");
  const [propertyFilter, setPropertyFilter] = useState("all");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [history, setHistory] = useState(null);

  const activeTenants = tenants.filter((t) => t.status === "ACTIVE");

  const rows = useMemo(() => {
    return activeTenants
      .filter((t) => (propertyFilter === "all" ? true : t.propertyId === propertyFilter))
      .filter((t) => {
        const status = computeTenantStatus(t, payments, month).toLowerCase();
        return filter === "all" ? true : status === filter;
      })
      .filter((t) => matchesQuery(query, t.name, t.phone, t.houseNumber));
  }, [activeTenants, payments, month, filter, propertyFilter, query]);

  const propertyOptions = [
    { value: "all", label: "All properties" },
    ...properties.map((p) => ({ value: p.id, label: p.name })),
  ];

  const totals = useMemo(() => {
    let collected = 0;
    let expected = 0;
    let outstanding = 0;
    let partialCount = 0;
    activeTenants.forEach((t) => {
      const paid = paidForMonth(payments, t.id, month);
      const rent = Number(t.rent) || 0;
      const balance = tenantBalance(t, payments, month);
      expected += rent;
      collected += Math.min(paid, rent); // cap for this month
      outstanding += balance;
      if (paid > 0 && paid < rent) partialCount++;
    });
    return { collected, expected, outstanding, partialCount };
  }, [activeTenants, payments, month]);

  const pct = totals.expected > 0
    ? Math.round((totals.collected / totals.expected) * 100)
    : 0;

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Rent Status</h2>
            <span className="rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-medium text-gray-700 dark:text-gray-300">
              {rows.length} {rows.length === 1 ? "tenant" : "tenants"}
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Track payments, pending balances and overdue tenants for {formatMonth(month)}.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            className="w-40"
            value={propertyFilter}
            onChange={setPropertyFilter}
            options={propertyOptions}
          />
          <Select
            className="w-44"
            value={month}
            onChange={setMonth}
            options={monthOptions()}
          />
          <Select
            className="w-44"
            value={filter}
            onChange={setFilter}
            options={STATUS_OPTIONS}
          />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard label="Collected" amount={totals.collected} tone="success" />
        <SummaryCard label="Outstanding" amount={totals.outstanding} tone="danger" />
        <SummaryCard label="Expected" amount={totals.expected} tone="default" />
        <div className="card p-5">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Collection rate
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">{pct}%</div>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
            <div
              className="h-full bg-indigo-600 transition-all duration-200"
              style={{ width: `${Math.min(100, pct)}%` }}
            />
          </div>
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {totals.partialCount} partial payment{totals.partialCount === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      {properties.length === 0 || activeTenants.length === 0 ? (
        <div className="card p-10 text-center text-sm text-gray-500 dark:text-gray-400">
          No active tenants to show.
        </div>
      ) : rows.length === 0 ? (
        <div className="card p-10 text-center text-sm text-gray-500 dark:text-gray-400">
          No tenants match this filter for {formatMonth(month)}.
        </div>
      ) : (
        <>
          <BulkActionsBar tenants={rows} />
          <TenantsTable
            tenants={rows}
            showProperty
            month={month}
            showAmountColumn
            onEdit={(t) => {
              setEditing(t);
              setEditorOpen(true);
            }}
            onOpenHistory={setHistory}
          />
        </>
      )}

      <TenantEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        editingTenant={editing}
        defaultPropertyId={properties[0]?.id}
      />
      <HistoryModal
        open={!!history}
        onClose={() => setHistory(null)}
        scope={history?.scope}
        tenant={history?.tenant}
      />
    </div>
  );
}

function SummaryCard({ label, amount, tone }) {
  const tones = {
    default: "text-gray-900 dark:text-white",
    success: "text-green-700 dark:text-green-400",
    danger: "text-red-700 dark:text-red-400",
  };
  return (
    <div className="card p-5">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
      <div className={`mt-2 text-2xl font-semibold ${tones[tone] || tones.default}`}>
        {formatKES(amount)}
      </div>
    </div>
  );
}
